import featureFlagStore from '../featureFlagStore';
import {
  BACKGROUND_COLOR_DEFAULT,
  TEXT_COLOR_DEFAULT,
  SPEAKER_LABEL_DEFAULT_TRANSITIONS,
  getSpeakerLabelBorderRadiusDefault,
  getSpeakerLabelDefaultContainerStyle,
  getSpeakerLabelDefaultNameStyle,
  getSpeakerLabelDefaultTitleStyle
} from '../constants';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { SpeakerLabelStyles } from '@/domains/asset';
import { FONT_DEFAULT_VALUE } from '@/Pages/Clip/SideBar/FontSelector/constants';

/**
 * Create speaker label styles for new clips based on current feature flag state
 * @param initialStyles - Initial style values to use
 * @returns Complete SpeakerLabelStyles object
 */
export function migrateSpeakerLabelStyles(initialSpeakerLabelStyles: Partial<SpeakerLabelStyles> | undefined) {
  const useAnimatedSpeakerCards = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Animated_Speaker_Cards];

  const hasNewCSSFields = !!(
    (initialSpeakerLabelStyles?.containerStyle && Object.keys(initialSpeakerLabelStyles.containerStyle).length > 0) ||
    (initialSpeakerLabelStyles?.nameStyle && Object.keys(initialSpeakerLabelStyles.nameStyle).length > 0) ||
    (initialSpeakerLabelStyles?.titleStyle && Object.keys(initialSpeakerLabelStyles.titleStyle).length > 0)
  );

  const borderRadius = initialSpeakerLabelStyles?.border_radius || getSpeakerLabelBorderRadiusDefault();
  const textColor = initialSpeakerLabelStyles?.text_color || TEXT_COLOR_DEFAULT;
  const backgroundColor = initialSpeakerLabelStyles?.background_color || BACKGROUND_COLOR_DEFAULT;

  // Create base style object (used in all cases)
  const baseStyles = {
    font_location: initialSpeakerLabelStyles?.font_location || FONT_DEFAULT_VALUE.url,
    text_color: textColor,
    background_color: backgroundColor,
    border_radius: borderRadius
  };

  // Create CSS style objects (used with animated speaker cards)
  const animatedSpeakerLabelStyles = {
    title_font_location: initialSpeakerLabelStyles?.title_font_location || baseStyles.font_location,
    containerStyle: {
      ...getSpeakerLabelDefaultContainerStyle(),
      minWidth: undefined,
      backgroundColor,
      borderRadius: getSpeakerLabelBorderRadiusDefault(),
      padding: '1em 1.5em',
      ...initialSpeakerLabelStyles?.containerStyle
    },
    nameStyle: {
      ...getSpeakerLabelDefaultNameStyle(),
      color: textColor,
      ...initialSpeakerLabelStyles?.nameStyle
    },
    titleStyle: {
      ...getSpeakerLabelDefaultTitleStyle(),
      color: textColor,
      ...initialSpeakerLabelStyles?.titleStyle
    },
    transitions: initialSpeakerLabelStyles?.transitions || SPEAKER_LABEL_DEFAULT_TRANSITIONS
  };

  if (!useAnimatedSpeakerCards) {
    // Flag OFF: Backward migration - strip CSS fields
    return baseStyles as SpeakerLabelStyles;
  }

  if (hasNewCSSFields) {
    // Already migrated: preserve existing CSS fields as-is
    return {
      ...baseStyles,
      title_font_location: initialSpeakerLabelStyles?.title_font_location,
      containerStyle: initialSpeakerLabelStyles?.containerStyle,
      nameStyle: initialSpeakerLabelStyles?.nameStyle,
      titleStyle: initialSpeakerLabelStyles?.titleStyle,
      transitions: initialSpeakerLabelStyles?.transitions || SPEAKER_LABEL_DEFAULT_TRANSITIONS
    } as SpeakerLabelStyles;
  }

  // Forward migration: apply defaults
  return { ...baseStyles, ...animatedSpeakerLabelStyles } as SpeakerLabelStyles;
}
